<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted, provide } from 'vue';
  import { onBeforeRouteLeave } from 'vue-router';
  // Components
  import {
    FieldSearchBar,
    SearchToolbar,
    SelectInput,
    NoDocuments,
    PaginateOrScroll,
  } from '@/shared/ui';
  import { DocumentFilterModal } from '@/features/Document';
  // Composables
  import { useApprovalOrVerificationSearch } from '@/features/Search';
  import { useFilterStore } from '@/entities/Filter';
  import { useTableFilterModal } from '@/shared/lib/use';
  // Misc
  import { QUANTITY_OPTIONS, ORDER_OPTIONS } from '@/shared/config';
  import { SEARCH_TYPE_FIELD_OPTIONS, useDocumentSearchTypeStore } from '@/shared/lib';
  import { SortOrderInjectionKey } from '@/shared/lib/sortOrder';

  interface Props {
    title: string;
    searchUrl: string;
    tableComponent: any;
    emptyStateTitle?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    emptyStateTitle: 'Nenhum documento pendente no momento',
  });

  const searchTerm = ref('');
  const selectedSort = ref({});
  const { searchType } = useDocumentSearchTypeStore();
  const filterParams = ref({
    revisionStatus: '',
    type: '',
    date: '',
    revision: '',
    user: '',
  });

  const searchParams = computed(() => ({
    search: searchTerm.value,
    searchType: searchType.value,
    sort: selectedSort.value,
    filter: filterParams.value,
    page: 1,
    amount: 10,
    searchUrl: props.searchUrl,
  }));

  const {
    items: revisions,
    metadata,
    selectedQuantity,
    allItems: allRevisions,
    hasMoreItems: hasMoreRevisions,
    loadingMore,
    isInfiniteScrollMode,
    shouldShowTable,
    shouldShowNoFilterResults,
    shouldShowNoResults,
    shouldShowEmptyPage,
    shouldShowQuantitySelect,
    handleSearch,
    handleFilter,
    handleSortOptions,
    onPageChanged,
    handleQuantityChange,
    initSearch,
    cleanup,
    filter,
    sort,
    order,
  } = useApprovalOrVerificationSearch(searchParams.value);

  provide(SortOrderInjectionKey, { sort, order });

  const filterStore = useFilterStore();

  const { isFilterActive, openFilterModal } = useTableFilterModal(
    DocumentFilterModal,
    filterStore,
    handleFilter,
    () => filter.value
  );

  const handleFilterClicked = () => {
    openFilterModal();
  };

  onMounted(async () => {
    await initSearch();
  });

  onUnmounted(() => {
    cleanup();
  });

  onBeforeRouteLeave(() => {
    filterStore.clearFilters();
  });
</script>

<template>
  <div class="pt-4 p-6">
    <h3 class="text-gray-07 text-xl/7.5 font-semibold text-left mb-6">
      {{ title }}
    </h3>

    <!-- Search Controls -->
    <SearchToolbar
      v-if="!shouldShowEmptyPage"
      class="mb-6"
    >
      <template #search>
        <FieldSearchBar
          :search-fields="SEARCH_TYPE_FIELD_OPTIONS"
          :initial-search-term="searchTerm"
          @search="handleSearch"
        />
      </template>
      <template
        v-if="shouldShowTable"
        #quantity
      >
        <SelectInput
          v-model="selectedQuantity"
          :options="QUANTITY_OPTIONS"
          class="cursor-pointer"
          @update:model-value="handleQuantityChange"
        />
      </template>
    </SearchToolbar>

    <PaginateOrScroll
      v-if="shouldShowTable"
      :items="revisions"
      :all-items="allRevisions"
      :has-more-items="hasMoreRevisions"
      :loading-more="loadingMore"
      :metadata="metadata"
      :selected-quantity="selectedQuantity"
      @page-changed="onPageChanged"
    >
      <template #table="{ items }">
        <component
          :is="tableComponent"
          :revisions="items"
          :is-filter-active="isFilterActive"
          :order-options="ORDER_OPTIONS"
          @update:sort="handleSortOptions"
          @update:filter="handleFilter"
          @filter-clicked="handleFilterClicked"
        />
      </template>
    </PaginateOrScroll>
    <NoDocuments
      v-if="shouldShowEmptyPage"
      :title="emptyStateTitle"
    />
  </div>
</template>
